/**
 * BroadcastPage.jsx
 * ESPN-style full-screen TV broadcast display.
 * Route: /tv/:tournamentId  (or /tv?t=tournamentId)
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import SportIcon from '../components/SportIcon';
import { VERSION_LABEL } from '../version';

const SPORT_ICONS = {
  table_tennis:'🏓', badminton:'🏸', chess:'♟️',
  carrom:'🎯', pickleball:'🥒', squash:'🎾',
};
const SPORT_SHORT = {
  table_tennis:'TT', badminton:'Bad.', chess:'Chess',
  carrom:'Carrom', pickleball:'PB', squash:'Sq.',
};

// ── Helpers ───────────────────────────────────────────────────
const fmtTime = (iso) => iso
  ? new Date(iso).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true,timeZone:'Asia/Kolkata'})
  : '';

/** Formats team name with seed label: "Asawa Dhadkans (A1)" or just "A1" for TBD */
const teamWithSeed = (name, seed) => {
  if (!name || name === 'TBD') return seed || 'TBD';
  return seed ? `${name} (${seed})` : name;
};

const TeamLogo = ({ team, size = 'md' }) => {
  const sz = size === 'lg' ? 'h-20 w-20 text-3xl' : size === 'sm' ? 'h-10 w-10 text-base' : 'h-14 w-14 text-xl';
  return (
    <div className={`${sz} rounded-2xl flex items-center justify-center font-black text-white flex-shrink-0 overflow-hidden`}
      style={{ background: `linear-gradient(135deg, ${team?.color||'#3b82f6'}, ${team?.color||'#3b82f6'}99)` }}>
      {team?.logo_url?.startsWith('data:')
        ? <img src={team.logo_url} className="h-full w-full object-cover" alt=""/>
        : <span>{team?.short_name?.[0]||team?.name?.[0]||'?'}</span>}
    </div>
  );
};

// ── Screen: Upcoming Schedule (upcoming matches, sport-wise) ──────
const ScheduleScreen = ({ encounters, tournament }) => {
  const now = new Date();
  const twoHrsLater = new Date(now.getTime() + 2 * 60 * 60 * 1000);

  // Filter to next 2 hours + ongoing
  const upcoming = encounters.filter(e => {
    if (!e.scheduled_at) return false;
    const t = new Date(e.scheduled_at);
    return (t >= now && t <= twoHrsLater) || e.status === 'in_progress';
  }).sort((a,b)=>a.scheduled_at.localeCompare(b.scheduled_at));

  // Build sport-wise player matchup list
  const sportItems = {};
  upcoming.forEach(enc => {
    (enc.sport_results||[]).forEach(sr => {
      const sc = sr.sport_code;
      if (!sportItems[sc]) sportItems[sc] = [];
      (sr.matches||[]).forEach(m => {
        const hp = m.home_player || (m.home_players?.[0] ? m.home_players.join(' / ') : null);
        const ap = m.away_player || (m.away_players?.[0] ? m.away_players.join(' / ') : null);
        sportItems[sc].push({
          time:      enc.scheduled_at,
          status:    enc.status,
          slot:      m.slot || m.label,
          homePlayer: hp || teamWithSeed(enc.home_team?.name, enc.home_seed) || 'TBD',
          awayPlayer: ap || teamWithSeed(enc.away_team?.name, enc.away_seed) || 'TBD',
          homeTeam:  teamWithSeed(enc.home_team?.name, enc.home_seed),
          awayTeam:  teamWithSeed(enc.away_team?.name, enc.away_seed),
          homeColor: enc.home_team?.color || '#3b82f6',
          awayColor: enc.away_team?.color || '#ef4444',
          winner:    m.winner,
          homeScore: m.home_score,
          awayScore: m.away_score,
        });
      });
    });
  });

  const sports = Object.keys(sportItems).sort();

  return (
    <div className="flex flex-col h-full p-8 gap-5">
      <div className="flex items-center gap-3">
        <span className="text-3xl">📅</span>
        <h2 className="text-3xl font-black text-white">Upcoming Matches</h2>
        <span className="text-gray-400 text-lg">{now.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true,timeZone:'Asia/Kolkata'})} — {twoHrsLater.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true,timeZone:'Asia/Kolkata'})}</span>
      </div>

      {sports.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500 text-2xl">No upcoming matches scheduled</p>
        </div>
      ) : (
        <div className="flex-1 overflow-hidden grid gap-6" style={{gridTemplateColumns:'repeat(auto-fit,minmax(380px,1fr))'}}>
          {sports.map(sc => (
            <div key={sc} className="flex flex-col">
              {/* Sport header */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl"><SportIcon sport={sc} size={54} /></span>
                <h3 className="text-xl font-black text-white">{sc.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}</h3>
              </div>

              {/* Player matchups */}
              <div className="space-y-1.5">
                {sportItems[sc].slice(0,8).map((item, i) => {
                  const slotColor = item.slot?.[0]==='P' ? '#f5c518' : '#3b82f6';
                  return (
                    <div key={i} className={`rounded-xl border px-4 py-2.5 ${
                      item.status==='in_progress' ? 'border-[rgba(239,68,68,0.4)] bg-[rgba(239,68,68,0.04)]' : 'border-[#2a2d3a] bg-[#14161f]'
                    }`}>
                      <div className="flex items-center gap-3">
                        {/* Time */}
                        <div className="flex-shrink-0 text-center w-14">
                          {item.status==='in_progress' ? (
                            <div className="text-[#ef4444] text-xs font-black flex items-center gap-0.5">
                              <span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-pulse"/>LIVE
                            </div>
                          ) : (
                            <p className="text-[#f5c518] font-bold text-xs tabular-nums">{fmtTime(item.time)}</p>
                          )}
                          {item.slot && (
                            <p className="text-[9px] font-bold mt-0.5" style={{color: slotColor}}>{item.slot}</p>
                          )}
                        </div>

                        {/* Home player + team */}
                        <div className="flex-1 min-w-0">
                          <p className={`font-bold text-sm truncate ${item.winner==='home'?'text-[#f5c518]':'text-white'}`}>
                            {item.homePlayer}
                          </p>
                          {item.homeTeam && item.homePlayer !== item.homeTeam && (
                            <p className="text-[10px] truncate" style={{color:item.homeColor}}>{item.homeTeam}</p>
                          )}
                        </div>

                        {/* Score or VS */}
                        <div className="flex-shrink-0 text-center w-14">
                          {item.winner ? (
                            <p className="text-sm font-black tabular-nums text-[#f5c518]">
                              {item.homeScore ?? 0}–{item.awayScore ?? 0}
                            </p>
                          ) : (
                            <p className="text-gray-600 font-bold text-sm">vs</p>
                          )}
                        </div>

                        {/* Away player + team */}
                        <div className="flex-1 min-w-0 text-right">
                          <p className={`font-bold text-sm truncate ${item.winner==='away'?'text-[#f5c518]':'text-white'}`}>
                            {item.awayPlayer}
                          </p>
                          {item.awayTeam && item.awayPlayer !== item.awayTeam && (
                            <p className="text-[10px] truncate" style={{color:item.awayColor}}>{item.awayTeam}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
// ── Screen: Live Matches ───────────────────────────────────────
const LiveScreen = ({ encounters, tournament }) => {
  const live = encounters.filter(e => e.status === 'in_progress');
  const upcoming = encounters.filter(e => e.status === 'scheduled').slice(0,3);
  const show = live.length > 0 ? live : upcoming;

  return (
    <div className="flex flex-col h-full p-8 gap-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="flex items-center gap-2 bg-[#ef4444] px-4 py-1.5 rounded-full">
          <span className="w-2.5 h-2.5 bg-white rounded-full animate-pulse"/>
          <span className="text-white font-black text-lg tracking-wide">LIVE</span>
        </div>
        <h2 className="text-2xl font-black text-white">{tournament?.name}</h2>
      </div>

      {show.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-400 text-2xl">No live matches right now</p>
        </div>
      ) : (
        <div className="flex-1 grid gap-5" style={{gridTemplateColumns:'repeat(auto-fit,minmax(380px,1fr))'}}>
          {show.slice(0,4).map(enc => {
            const isDone = enc.status === 'completed';
            const isLive = enc.status === 'in_progress';
            const ht = enc.home_team || {};
            const at = enc.away_team || {};
            const sports = enc.sport_results || [];
            return (
              <div key={enc.id} className={`rounded-3xl border-2 overflow-hidden flex flex-col ${
                isLive ? 'border-[#ef4444] shadow-[0_0_40px_rgba(239,68,68,0.25)]' : 'border-[#2a2d3a]'
              }`} style={{background:'linear-gradient(135deg,#14161f,#1a1d2e)'}}>
                {/* Match label */}
                <div className="flex items-center justify-between px-5 pt-4 pb-2">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                    {enc.match_label || enc.group_letter || ''}
                  </span>
                  {isLive && <span className="text-[#ef4444] text-xs font-bold flex items-center gap-1">
                    <span className="w-2 h-2 bg-[#ef4444] rounded-full animate-pulse"/>LIVE
                  </span>}
                  {!isLive && enc.scheduled_at && (
                    <span className="text-gray-400 text-xs">{fmtTime(enc.scheduled_at)}</span>
                  )}
                </div>

                {/* Teams + score */}
                <div className="flex items-center justify-between px-5 py-4 gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <TeamLogo team={ht} size="md"/>
                    <p className={`font-black text-lg leading-tight truncate ${enc.winner==='home'?'text-[#f5c518]':'text-white'}`}>
                      {teamWithSeed(ht.name, enc.home_seed)}
                    </p>
                  </div>
                  <div className="text-center flex-shrink-0">
                    {isLive || isDone ? (
                      <div className="text-4xl font-black tabular-nums" style={{color:'#f5c518',textShadow:'0 0 20px rgba(245,197,24,0.5)'}}>
                        {enc.total_home_pts ?? 0}<span className="text-gray-500 mx-1">:</span>{enc.total_away_pts ?? 0}
                      </div>
                    ) : (
                      <div className="text-3xl font-black text-gray-500">VS</div>
                    )}
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">Points</p>
                  </div>
                  <div className="flex items-center gap-3 flex-1 min-w-0 justify-end">
                    <p className={`font-black text-lg leading-tight truncate text-right ${enc.winner==='away'?'text-[#f5c518]':'text-white'}`}>
                      {teamWithSeed(at.name, enc.away_seed)}
                    </p>
                    <TeamLogo team={at} size="md"/>
                  </div>
                </div>

                {/* Sport scores strip */}
                {(isLive||isDone) && sports.filter(s=>s.sport_winner||s.home_match_wins>0||s.away_match_wins>0).length > 0 && (
                  <div className="px-5 pb-4 flex gap-2 flex-wrap">
                    {sports.map(s => (
                      <div key={s.sport_code} className="flex items-center gap-1 bg-[#0f1117] rounded-full px-3 py-1">
                        <SportIcon sport={s.sport_code} size={16}/>
                        <span className="text-white text-xs font-bold tabular-nums">
                          {s.home_match_wins ?? 0}–{s.away_match_wins ?? 0}
                        </span>
                        {s.sport_winner && <span className={`text-[10px] font-black ${s.sport_winner==='draw'?'text-[#f5c518]':s.sport_winner==='home'?'text-[#22c55e]':'text-[#ef4444]'}`}>
                          {s.sport_winner==='draw'?'D':s.sport_winner==='home'?'▲':'▼'}
                        </span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Screen: Standings ─────────────────────────────────────────
const StandingsScreen = ({ standings, tournament }) => {
  // Collect all sports across groups
  const allSports = [...new Set(standings.flatMap(g => g.sports || []))];
  // Rotate sport every ~5s within the 15s standings screen
  const [sportIdx, setSportIdx] = useState(0);
  useEffect(() => {
    if (allSports.length <= 1) return;
    const t = setInterval(() => setSportIdx(i => (i + 1) % allSports.length), 5000);
    return () => clearInterval(t);
  }, [allSports.length]);
  const activeSport = allSports[sportIdx] || null;

  return (
    <div className="flex flex-col h-full p-8 gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {activeSport && <span className="text-4xl"><SportIcon sport={activeSport} size={60} /></span>}
          <div>
            <h2 className="text-3xl font-black text-white">
              {activeSport
                ? (activeSport.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) + ' Standings')
                : `${tournament?.name} — Standings`}
            </h2>
            <p className="text-gray-500 text-sm">Points per tie win · {tournament?.name}</p>
          </div>
        </div>
        {/* Sport rotation indicator */}
        {allSports.length > 1 && (
          <div className="flex gap-2">
            {allSports.map((sc, i) => (
              <span key={sc} className={`text-2xl transition-all ${i === sportIdx ? 'scale-125 opacity-100' : 'opacity-30'}`}>
                <SportIcon sport={sc} size={54} />
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Per-sport group tables side by side */}
      {activeSport && (
        <div className="flex-1 grid gap-6" style={{gridTemplateColumns:`repeat(${Math.min(standings.length,3)},1fr)`}}>
          {standings.map(grp => {
            const sorted = [...grp.standings]
              .sort((a, b) => (b.sport_pts?.[activeSport] ?? 0) - (a.sport_pts?.[activeSport] ?? 0));
            return (
              <div key={grp.group} className="rounded-3xl overflow-hidden border border-[#2a2d3a]"
                style={{background:'linear-gradient(135deg,#14161f,#1a1d2e)'}}>
                <div className="px-6 py-4 border-b border-[#2a2d3a]">
                  <h3 className="text-2xl font-black text-white">{grp.name}</h3>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="text-gray-500 text-xs uppercase tracking-widest">
                      <th className="px-6 py-3 text-left">#</th>
                      <th className="px-3 py-3 text-left">Team</th>
                      <th className="px-6 py-3 text-center text-[#f5c518]">Pts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((row, i) => (
                      <tr key={row.team_id} className={`border-t border-[#1a1d2e] ${i===0?'bg-[rgba(245,197,24,0.06)]':''}`}>
                        <td className="px-6 py-4">
                          <span className={`text-2xl ${i===0?'':'opacity-50'}`}>
                            {i===0?'🥇':i===1?'🥈':i===2?'🥉':i+1}
                          </span>
                        </td>
                        <td className="px-3 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-xl flex items-center justify-center font-black text-white text-sm flex-shrink-0 overflow-hidden"
                              style={{background:`linear-gradient(135deg,${row.color||'#3b82f6'},${row.color||'#3b82f6'}88)`}}>
                              {row.logo_url?.startsWith('data:')
                                ? <img src={row.logo_url} className="h-full w-full rounded-xl object-cover" alt=""/>
                                : row.team_name?.[0]}
                            </div>
                            <span className={`font-bold text-xl ${i===0?'text-[#f5c518]':'text-white'}`}>{row.team_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`font-black tabular-nums ${i===0?'text-4xl text-[#f5c518]':'text-2xl text-gray-300'}`}
                            style={i===0?{textShadow:'0 0 20px rgba(245,197,24,0.5)'}:{}}>
                            {row.sport_pts?.[activeSport] ?? 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Screen: Recent Results ────────────────────────────────────
const ResultsScreen = ({ encounters, tournament }) => {
  const done = encounters.filter(e => e.status === 'completed' || e.winner).slice(-8).reverse();
  return (
    <div className="flex flex-col h-full p-8 gap-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-4xl font-black text-white tracking-tight">Recent Results</h2>
        <span className="text-gray-500 text-lg">{tournament?.name}</span>
      </div>

      <div className="flex-1 flex flex-col gap-3">
        {done.map((enc, ri) => {
          const ht = enc.home_team || {};
          const at = enc.away_team || {};
          const hw = enc.winner === 'home';
          const aw = enc.winner === 'away';
          const hPts = enc.total_home_pts ?? 0;
          const aPts = enc.total_away_pts ?? 0;
          const hn = teamWithSeed(ht.name, enc.home_seed);
          const an = teamWithSeed(at.name, enc.away_seed);
          const hc = ht.color || '#3b82f6';
          const ac = at.color || '#ef4444';

          return (
            <div key={enc.id}
              className="flex items-stretch rounded-2xl overflow-hidden border border-[#1e2435]"
              style={{ flex: '1', minHeight: 0 }}>

              {/* Home side */}
              <div className={`flex items-center gap-4 px-6 flex-1 transition-all ${hw ? 'bg-[#1a1d2e]' : 'bg-[#0f1117]'}`}
                style={{ borderLeft: `4px solid ${hw ? hc : 'transparent'}` }}>
                <div className="h-14 w-14 rounded-2xl flex-shrink-0 overflow-hidden flex items-center justify-center font-black text-white text-2xl"
                  style={{ background: `linear-gradient(135deg,${hc},${hc}88)` }}>
                  {ht.logo_url?.startsWith('data:')
                    ? <img src={ht.logo_url} className="h-full w-full object-cover" alt=""/>
                    : hn[0]}
                </div>
                <div className="min-w-0">
                  <p className={`font-black text-xl leading-tight truncate ${hw ? 'text-white' : 'text-gray-400'}`}>{hn}</p>
                  {hw && <p className="text-[#f5c518] text-sm font-bold mt-0.5">🏆 Winner</p>}
                </div>
              </div>

              {/* Score */}
              <div className={`flex flex-col items-center justify-center px-8 flex-shrink-0 ${enc.winner ? 'bg-[#0a0c12]' : 'bg-[#0f1117]'}`}>
                <div className="flex items-center gap-4">
                  <span className={`text-5xl font-black tabular-nums ${hw ? 'text-white' : 'text-gray-500'}`}
                    style={hw ? {textShadow:`0 0 20px ${hc}60`} : {}}>
                    {hPts}
                  </span>
                  <span className="text-2xl text-gray-600 font-bold">–</span>
                  <span className={`text-5xl font-black tabular-nums ${aw ? 'text-white' : 'text-gray-500'}`}
                    style={aw ? {textShadow:`0 0 20px ${ac}60`} : {}}>
                    {aPts}
                  </span>
                </div>
                <p className="text-gray-600 text-xs mt-1 uppercase tracking-widest font-semibold">
                  {enc.match_label || enc.round_label || enc.round_type || 'Group'}
                </p>
              </div>

              {/* Away side */}
              <div className={`flex items-center gap-4 px-6 flex-1 justify-end transition-all ${aw ? 'bg-[#1a1d2e]' : 'bg-[#0f1117]'}`}
                style={{ borderRight: `4px solid ${aw ? ac : 'transparent'}` }}>
                <div className="min-w-0 text-right">
                  <p className={`font-black text-xl leading-tight truncate ${aw ? 'text-white' : 'text-gray-400'}`}>{an}</p>
                  {aw && <p className="text-[#f5c518] text-sm font-bold mt-0.5">🏆 Winner</p>}
                </div>
                <div className="h-14 w-14 rounded-2xl flex-shrink-0 overflow-hidden flex items-center justify-center font-black text-white text-2xl"
                  style={{ background: `linear-gradient(135deg,${ac},${ac}88)` }}>
                  {at.logo_url?.startsWith('data:')
                    ? <img src={at.logo_url} className="h-full w-full object-cover" alt=""/>
                    : an[0]}
                </div>
              </div>
            </div>
          );
        })}
        {done.length === 0 && (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-600 text-2xl">No results yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Congratulations overlay ────────────────────────────────────
const CongratsOverlay = ({ enc, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 5000);
    return () => clearTimeout(t);
  }, [onDone]);

  const winner = enc.winner === 'home' ? enc.home_team : enc.away_team;
  const loser  = enc.winner === 'home' ? enc.away_team : enc.home_team;
  const hPts = enc.total_home_pts ?? 0;
  const aPts = enc.total_away_pts ?? 0;
  const winPts = enc.winner==='home' ? hPts : aPts;
  const losPts = enc.winner==='home' ? aPts : hPts;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{background:'radial-gradient(ellipse at center,rgba(245,197,24,0.15) 0%,rgba(0,0,0,0.95) 70%)'}}>
      {/* Confetti-like particles */}
      {[...Array(20)].map((_,i) => (
        <div key={i} className="absolute rounded-full animate-bounce"
          style={{
            width: Math.random()*12+4,
            height: Math.random()*12+4,
            left: `${Math.random()*100}%`,
            top: `${Math.random()*100}%`,
            backgroundColor: ['#f5c518','#ef4444','#22c55e','#3b82f6','#8b5cf6'][i%5],
            animationDuration: `${Math.random()*1+0.5}s`,
            animationDelay: `${Math.random()*0.5}s`,
            opacity: 0.7,
          }}/>
      ))}
      <div className="text-center space-y-6 px-8">
        <p className="text-[#f5c518] text-2xl font-bold uppercase tracking-[0.3em] animate-pulse">
          🏆 Result 🏆
        </p>
        <div className="flex items-center gap-8 justify-center">
          <TeamLogo team={winner} size="lg"/>
          <div>
            <p className="text-6xl font-black text-white mb-1" style={{textShadow:'0 0 40px rgba(245,197,24,0.6)'}}>
              {winPts} – {losPts}
            </p>
            <p className="text-gray-400 text-lg">Final Score</p>
          </div>
          <TeamLogo team={loser} size="lg"/>
        </div>
        <div>
          <p className="text-5xl font-black mb-2" style={{color:'#f5c518',textShadow:'0 0 30px rgba(245,197,24,0.5)'}}>
            {winner?.name || 'Winner'}
          </p>
          <p className="text-xl text-gray-300">wins against {loser?.name || 'Opponent'}</p>
          {enc.match_label && <p className="text-gray-500 text-base mt-1">{enc.match_label}</p>}
        </div>
        <p className="text-gray-600 text-sm">Screen continues in 5 seconds…</p>
      </div>
    </div>
  );
};

// ── Sponsors Strip ─────────────────────────────────────────────

// ── Sponsors strip — static 2 rows of 4, all 8 visible at once ─
const SponsorsStrip = ({ sponsors }) => {
  if (!sponsors || sponsors.length === 0) return null;
  const mid  = Math.ceil(sponsors.length / 2);
  const row1 = sponsors.slice(0, mid);
  const row2 = sponsors.slice(mid);

  const SponsorCard = ({ sp }) => (
    <div className="flex items-center gap-2 flex-1 min-w-0 px-2">
      {(sp.logo_url || sp.team_logo_url) ? (
        <img
          src={sp.logo_url || sp.team_logo_url}
          alt={sp.team_name || sp.name}
          className="h-8 w-8 rounded-xl object-contain flex-shrink-0 bg-white/5 p-0.5"
          onError={e => { e.currentTarget.style.display='none'; }}
        />
      ) : (
        <div className="h-8 w-8 rounded-xl flex items-center justify-center font-black text-white text-sm flex-shrink-0"
          style={{background:`linear-gradient(135deg,${sp.team_color||'#3b82f6'},${sp.team_color||'#3b82f6'}88)`}}>
          {sp.name?.[0]?.toUpperCase() || 'S'}
        </div>
      )}
      <div className="min-w-0">
        {sp.team_name && (
          <p className="font-black text-xs leading-tight truncate"
            style={{color: sp.team_color || '#f5c518'}}>
            {sp.team_name}
          </p>
        )}
        <p className="text-gray-400 text-[10px] leading-tight truncate">{sp.name}</p>
      </div>
    </div>
  );

  return (
    <div className="fixed z-30"
      style={{
        bottom: '56px', left: 0, right: 0,
        height: '88px',
        background: 'linear-gradient(90deg,#0a0c12,#111318,#0a0c12)',
        borderTop: '1px solid rgba(245,197,24,0.2)',
      }}>
      <div className="flex h-full">
        {/* OUR SPONSORS label */}
        <div className="flex-shrink-0 w-[90px] flex items-center justify-center border-r border-[rgba(245,197,24,0.2)]"
          style={{background:'rgba(245,197,24,0.05)'}}>
          <p className="text-[#f5c518] text-[8px] font-black uppercase tracking-widest text-center leading-tight px-1">
            OUR<br/>SPONSORS
          </p>
        </div>
        {/* Two static rows */}
        <div className="flex-1 flex flex-col divide-y divide-[rgba(245,197,24,0.08)] overflow-hidden">
          <div className="flex items-center flex-1 divide-x divide-[rgba(255,255,255,0.04)]">
            {row1.map((sp, i) => <SponsorCard key={i} sp={sp}/>)}
          </div>
          {row2.length > 0 && (
            <div className="flex items-center flex-1 divide-x divide-[rgba(255,255,255,0.04)]">
              {row2.map((sp, i) => <SponsorCard key={i} sp={sp}/>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Ticker ─────────────────────────────────────────────────────
const Ticker = ({ items }) => {
  if (!items.length) return null;
  const text = items.join('   ◆   ');
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 overflow-hidden"
      style={{height:'56px',background:'linear-gradient(90deg,#0a0c12,#111318,#0a0c12)',borderTop:'2px solid #f5c518'}}>
      <div className="flex items-center h-full">
        {/* Scrolling text */}
        <div className="flex-1 overflow-hidden relative h-full">
          <div className="whitespace-nowrap animate-[scroll_36s_linear_infinite] flex items-center h-full">
            <span className="text-white text-base font-semibold px-8">{text}</span>
            <span className="text-white text-base font-semibold px-8" aria-hidden>{text}</span>
          </div>
        </div>
        {/* Attribution */}
        <div className="flex-shrink-0 px-5 h-full flex items-center border-l border-[rgba(245,197,24,0.3)]">
          <span className="text-[#8a93a3] text-xs font-medium tracking-wide whitespace-nowrap">
            Software developed by <span className="text-[#f5c518] font-semibold">Sandeep Maheshwari</span>
          </span>
        </div>
      </div>
    </div>
  );
};

// ── Main Broadcast Page ────────────────────────────────────────
export default function BroadcastPage() {
  const { tournamentId: tidParam } = useParams();
  const [searchParams] = useSearchParams();
  const tid = tidParam || searchParams.get('t') || '';

  const [tournament, setTournament]     = useState(null);
  const [encounters, setEncounters]     = useState([]);
  const [standings, setStandings]       = useState([]);
  const [sponsors, setSponsors]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [screen, setScreen]             = useState(0);
  const [congrats, setCongrats]         = useState(null);
  const [tickerItems, setTickerItems]   = useState([]);
  const prevCompletedRef = useRef(new Set());
  const clockRef = useRef(null);
  const [clockTime, setClockTime]       = useState(new Date());

  const SCREENS = ['live', 'standings', 'schedule', 'results'];

  // Build ticker from encounters
  const buildTicker = useCallback((encs, tourn) => {
    const items = [];
    if (tourn) items.push(`📍 ${tourn.name}  ${tourn.venue_name ? `| ${tourn.venue_name}` : ''}`);
    const live = encs.filter(e => e.status === 'in_progress');
    live.forEach(e => {
      const h = e.home_team?.name||e.home_seed||'H';
      const a = e.away_team?.name||e.away_seed||'A';
      items.push(`🔴 LIVE: ${h} ${e.total_home_pts??0} – ${e.total_away_pts??0} ${a}`);
    });
    const done = encs.filter(e => e.status === 'completed').slice(-5);
    done.forEach(e => {
      const h = e.home_team?.name||e.home_seed||'H';
      const a = e.away_team?.name||e.away_seed||'A';
      const winner = e.winner==='home'?h:a;
      items.push(`✅ ${h} ${e.total_home_pts??0} – ${e.total_away_pts??0} ${a}  │  ${winner} wins`);
    });
    setTickerItems(items);
  }, []);

  const fetchAll = useCallback(async () => {
    if (!tid) return;
    try {
      const [tRes, gsRes, encRes, spRes] = await Promise.allSettled([
        api.get(`/public/tournaments/${tid}`),
        api.get(`/public/tournaments/${tid}/group-standings`),
        api.get(`/public/tournaments/${tid}/encounters`),
        api.get(`/public/tournaments/${tid}/sponsors`),
      ]);
      const t   = tRes.status==='fulfilled' ? tRes.value.data : null;
      const gs  = gsRes.status==='fulfilled' && Array.isArray(gsRes.value.data) ? gsRes.value.data : [];
      const encs= encRes.status==='fulfilled' && Array.isArray(encRes.value.data) ? encRes.value.data : [];
      const sps = spRes.status==='fulfilled' && Array.isArray(spRes.value.data) ? spRes.value.data : [];
      if (t)   setTournament(t);
      if (gs)  setStandings(gs);
      if (sps) setSponsors(sps);
      if (encs) {
        // Detect newly completed encounters
        const newlyDone = encs.filter(e =>
          e.status === 'completed' && e.winner &&
          !prevCompletedRef.current.has(e.id)
        );
        if (newlyDone.length > 0 && prevCompletedRef.current.size > 0) {
          setCongrats(newlyDone[0]);
        }
        prevCompletedRef.current = new Set(encs.filter(e=>e.status==='completed').map(e=>e.id));
        setEncounters(encs);
        buildTicker(encs, t);
      }
    } catch (_) {}
    finally { setLoading(false); }
  }, [tid, buildTicker]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-rotate screens every 15s
  useEffect(() => {
    const iv = setInterval(() => {
      setScreen(s => (s + 1) % SCREENS.length);
      fetchAll();
    }, 15000);
    return () => clearInterval(iv);
  }, [fetchAll]);

  // Clock update
  useEffect(() => {
    clockRef.current = setInterval(() => setClockTime(new Date()), 1000);
    return () => clearInterval(clockRef.current);
  }, []);

  const currentScreen = SCREENS[screen];
  const liveCount = encounters.filter(e => e.status === 'in_progress').length;
  // Prefer title sponsor WITH a logo; fall back to any title sponsor
  const titleSponsor = sponsors.find(s =>
    s.type_id?.toLowerCase().includes('title') && (s.logo_url || s.team_logo_url)
  ) || sponsors.find(s =>
    s.type_id?.toLowerCase().includes('title')
  ) || null;

  // Deduplicate sponsors by team_name (or name for those without team_name).
  // Production may have duplicate entries — each team should appear exactly once.
  // Also exclude title sponsors from the strip (they show in the top bar).
  const dedupedSponsors = (() => {
    const seen = new Set();
    return sponsors.filter(sp => {
      // Skip title sponsors — shown in top bar already
      if (sp.type_id?.toLowerCase().includes('title')) return false;
      const key = (sp.team_name?.trim().toLowerCase()) ||
                  (sp.name?.trim().toLowerCase()) ||
                  sp.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  return (
    <div className="fixed inset-0 overflow-hidden text-white select-none"
      style={{
        background:'#0a0c12',
        fontFamily:"'Segoe UI', system-ui, -apple-system, sans-serif",
        zoom: 1.5,   /* 50% scale-up of ALL content for TV readability */
      }}>

      {/* CSS for ticker scroll */}
      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes sponsorScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      {/* Top bar */}
      <div className="flex items-center justify-between px-8 py-3 border-b border-[#1e2230]"
        style={{background:'linear-gradient(90deg,#0f111a,#141620)', minHeight:'80px'}}>
        <div className="flex items-center gap-4 min-w-0">
          <span className="text-[#f5c518] font-black text-2xl tracking-wider flex-shrink-0">BTCCP</span>
          <div className="w-px h-10 bg-[#2a2d3a] flex-shrink-0"/>
          {/* Title sponsor + tournament name */}
          <div className="min-w-0">
            {titleSponsor ? (
              <div className="flex items-center gap-4">
                {(titleSponsor.logo_url || titleSponsor.team_logo_url) && (
                  <img
                    src={titleSponsor.logo_url || titleSponsor.team_logo_url}
                    className="h-20 w-20 rounded-2xl object-contain flex-shrink-0 bg-white p-1"
                    alt={titleSponsor.name}
                    onError={e => { e.currentTarget.style.display='none'; }}
                  />
                )}
                <div>
                  <p className="text-[#f5c518] text-xl font-black uppercase tracking-widest leading-tight">
                    {titleSponsor.name} presents
                  </p>
                  <p className="text-white font-bold text-base leading-tight">{tournament?.name}</p>
                </div>
              </div>
            ) : (
              <span className="text-gray-300 text-lg font-bold">{tournament?.name || 'Loading…'}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          {/* Brand logos */}
          <div className="flex items-center gap-2">
            <img src="/circles-logo.png" alt="Circles"
              className="h-16 w-16 rounded-full object-cover border-2 border-[#2a2d3a]"
              onError={e => { e.currentTarget.style.display='none'; }}/>
            <img src="/c7-logo.png" alt="C7"
              className="h-16 w-16 rounded-full object-cover border-2 border-[#2a2d3a]"
              onError={e => { e.currentTarget.style.display='none'; }}/>
          </div>
          <div className="w-px h-10 bg-[#2a2d3a]"/>
          {liveCount > 0 && (
            <div className="flex items-center gap-2 bg-[#ef4444] px-3 py-1 rounded-full">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse"/>
              <span className="text-white text-xs font-black">{liveCount} LIVE</span>
            </div>
          )}
          <div className="flex gap-1.5">
            {SCREENS.map((_,i) => (
              <div key={i} className={`h-1.5 rounded-full transition-all ${i===screen?'w-6 bg-[#f5c518]':'w-1.5 bg-[#2a2d3a]'}`}/>
            ))}
          </div>
          <span className="text-[#f5c518] font-black text-xl tabular-nums">
            {clockTime.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:true,timeZone:'Asia/Kolkata'})}
          </span>
          <span className="text-gray-700 text-[10px] font-medium tracking-wide hidden sm:inline">
            @Sandeep Maheshwari · <span className="font-mono">{VERSION_LABEL}</span>
          </span>
        </div>
      </div>

      {/* Main content area */}
      <div className={`absolute inset-0 top-[56px] overflow-hidden`}
        style={{bottom: dedupedSponsors.length > 0 ? '144px' : '56px'}}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-4">
              <div className="text-6xl font-black text-[#f5c518] animate-pulse">BTCCP</div>
              <p className="text-gray-400 text-xl">Loading scoreboard…</p>
            </div>
          </div>
        ) : (
          <div className="h-full overflow-hidden">
            {currentScreen === 'live'      && <LiveScreen     encounters={encounters} tournament={tournament}/>}
            {currentScreen === 'standings' && <StandingsScreen standings={standings}  tournament={tournament}/>}
            {currentScreen === 'schedule'  && <ScheduleScreen  encounters={encounters} tournament={tournament}/>}
            {currentScreen === 'results'   && <ResultsScreen   encounters={encounters} tournament={tournament}/>}
          </div>
        )}
      </div>

      {/* Sponsors scrolling strip — show ALL sponsors (no filtering), deduplicated by team */}
      <SponsorsStrip sponsors={dedupedSponsors}/>

      {/* Results Ticker */}
      <Ticker items={tickerItems}/>

      {/* Attribution — always visible bottom-right */}
      <div className="absolute bottom-[52px] right-4 z-20 pointer-events-none">
        <p className="text-[11px] text-gray-600 font-medium tracking-wide">
          Software by <span className="text-gray-500">@Sandeep Maheshwari</span>
        </p>
      </div>

      {/* Congratulations overlay */}
      {congrats && (
        <CongratsOverlay enc={congrats} onDone={() => setCongrats(null)}/>
      )}

      {/* No tournament ID prompt */}
      {!tid && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0a0c12] z-50">
          <div className="text-center space-y-4">
            <p className="text-4xl font-black text-white">BTCCP Broadcast</p>
            <p className="text-gray-400 text-lg">Visit <code className="text-[#f5c518]">/tv/{'{'}{'}'}tournamentId</code></p>
          </div>
        </div>
      )}
    </div>
  );
}
